import { Button } from "@material-ui/core"
import DeleteOutline from "@material-ui/icons/DeleteOutline"
import { divIcon, marker } from "leaflet"
import type { DivIcon, LatLng, LeafletEvent, Map as LeafletMap, LeafletMouseEvent } from "leaflet"
import { useEffect, useState } from "react"
import type { ReactElement } from "react"
import { renderToString } from "react-dom/server"
import { useTranslation } from "react-i18next"
import { Marker, Tooltip, useMap } from "react-leaflet"
import { AttachmentPinIcon, Caption } from ".."
import { usePrevious } from "../../../hooks"

import "./map-selection-marker.styles.scss"

export interface IMapSelectionMarker {
  position: LatLng
  hasMoved: boolean
  onMove: (position?: LatLng) => void
}

export function MapSelectionMarker(props: IMapSelectionMarker): ReactElement {
  const { position, onMove, hasMoved } = props
  const { t } = useTranslation(["general", "map"])

  const [positionMarker, setPositionMarker] = useState<L.Marker>()
  const previousHasMoved: boolean | undefined = usePrevious<boolean>(hasMoved)

  const [isOverMarker, setIsOverMarker] = useState<boolean>(false)
  const [isOverTooltip, setIsOverTooltip] = useState<boolean>(false)
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false)
  const map: LeafletMap = useMap()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    setPositionMarker(createMarker())

    if (hasMoved) {
      document.querySelector(".attachment-icon.selected")?.classList.remove("selected")
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (previousHasMoved && !hasMoved && positionMarker) {
      map.removeLayer(positionMarker)
      setPositionMarker(createMarker())
    }

    if (!previousHasMoved && hasMoved && positionMarker) {
      document.querySelector(".attachment-icon.selected")?.classList.remove("selected")
    }
  }, [hasMoved])

  useEffect(() => {
    setTooltipVisible(isOverMarker || isOverTooltip)
  }, [isOverMarker, isOverTooltip])

  function getMarkerIcon(): DivIcon {
    return divIcon({
      className: "attachment-icon selected",
      html: renderToString(<AttachmentPinIcon />)
    })
  }

  function getCircleMarkerIcon(): DivIcon {
    return divIcon({
      className: "attachment-icon-circle",
      html: '<div class="circle-pulse"></div>'
    })
  }

  function getEmptyMarkerIcon(): DivIcon {
    return divIcon({
      className: "attachment-delete-button-marker",
      html: "<div></div>"
    })
  }

  function createMarker(): L.Marker {
    return marker(position, {
      icon: getMarkerIcon(),
      draggable: true,
      zIndexOffset: 1000
    })
      .on("move", (event: LeafletEvent) => {
        onMove((event as LeafletMouseEvent).latlng)
        setTooltipVisible(false)
      })
      .on("dragend", () => {
        setTooltipVisible(true)
      })
      .on("mouseover", () => {
        setIsOverMarker(true)
      })
      .on("mouseout", () => {
        setTimeout(() => {
          setIsOverMarker(false)
        }, 10)
      })
      .addTo(map)
  }

  return !hasMoved ? (
    <Marker position={position} icon={getCircleMarkerIcon()}>
      <Tooltip
        permanent={true}
        direction="top"
        className="map-selection-marker-tooltip"
        position={position}
        key="map-selection-marker-tooltip"
      >
        <Caption>{t("map:attachments.location.dragPinLocation")}</Caption>
      </Tooltip>
    </Marker>
  ) : (
    <Marker position={position} icon={getEmptyMarkerIcon()}>
      {tooltipVisible && (
        <Tooltip
          direction="right"
          className="attachment-delete-button-marker-tooltip"
          key="attachment-delete-button-marker-tooltip"
          permanent={tooltipVisible}
          interactive={true}
        >
          <Button
            onMouseEnter={() => setIsOverTooltip(true)}
            onMouseLeave={() => setTimeout(() => setIsOverTooltip(false), 10)}
            onClick={() => onMove(undefined)}
          >
            <DeleteOutline htmlColor="#CF0101" />
            {t("general:dialog.delete").toLowerCase()}
          </Button>
        </Tooltip>
      )}
    </Marker>
  )
}
